import { useMutation } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import {
  LIST_USAGE_TYPES,
  UNSUITABLE_REASON_KEYS,
} from "@/components/shared/business/UnsuitableReason/constants/unsuitableReason.constants";
import { UnsuitableReasonUsageType } from "@/components/shared/business/UnsuitableReason/types/unsuitableReason.types";
import { queryClient } from "@api/queryClient";
import { QUERY_KEYS } from "@api/queryKeys";
import { ROUTE_PATHS } from "@routes/routePath";
import { getPressIdFromURL } from "@utils/getStateFromURL";
import { toastFail, toastSuccess } from "@utils/toast";

import { unsuitablePressApis } from "./unsuitablePress.service";

const TOAST_POSITION_BY_USAGE_TYPE: Record<
  UnsuitableReasonUsageType,
  "topCenter" | "bottomLeft"
> = {
  [UNSUITABLE_REASON_KEYS.pressList]: "bottomLeft",
  [UNSUITABLE_REASON_KEYS.ctgList]: "bottomLeft",
  [UNSUITABLE_REASON_KEYS.publicationList]: "bottomLeft",
  [UNSUITABLE_REASON_KEYS.pressDetail]: "topCenter",
  [UNSUITABLE_REASON_KEYS.ctgDetail]: "topCenter",
  [UNSUITABLE_REASON_KEYS.regulatoryList]: "bottomLeft",
  [UNSUITABLE_REASON_KEYS.regulatoryDetail]: "topCenter",
  [UNSUITABLE_REASON_KEYS.publicationDetail]: "topCenter",
};

export const useUnsuitablePressCancelMutation = (
  unsuitableReasonUsageType: UnsuitableReasonUsageType,
) => {
  const toastPosition = TOAST_POSITION_BY_USAGE_TYPE[unsuitableReasonUsageType];

  const QUERY_KEYS_BY_USAGE_TYPE: Record<
    UnsuitableReasonUsageType,
    [string, number]
  > = {
    [UNSUITABLE_REASON_KEYS.pressList]: [QUERY_KEYS.articleInfo, 0],
    [UNSUITABLE_REASON_KEYS.ctgList]: [QUERY_KEYS.articleInfo, 0],
    [UNSUITABLE_REASON_KEYS.publicationList]: [QUERY_KEYS.articleInfo, 0],
    [UNSUITABLE_REASON_KEYS.pressDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.ctgDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.regulatoryList]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.regulatoryDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
    [UNSUITABLE_REASON_KEYS.publicationDetail]: [
      QUERY_KEYS.articleInfo,
      Number(getPressIdFromURL()),
    ],
  };

  const currentInfoQueryKey =
    QUERY_KEYS_BY_USAGE_TYPE[unsuitableReasonUsageType];

  const mutation = useMutation({
    mutationFn: (draftArticleId: number) => {
      return unsuitablePressApis.put(String(draftArticleId));
    },
    onSuccess: async () => {
      if (!LIST_USAGE_TYPES.includes(unsuitableReasonUsageType)) {
        await queryClient.refetchQueries({
          queryKey: currentInfoQueryKey,
        });
      }
      toastSuccess(
        "Unsuitable press successfully released",
        "green",
        toastPosition,
      );
    },
    onError: () => {
      toastFail("Failed to update unsuitable reason", toastPosition);
    },
  });

  return mutation;
};

export const useOverlappedPressCancelMutation = () => {
  const location = useLocation();
  const isPressList = location.pathname === ROUTE_PATHS.pressList;

  const mutation = useMutation({
    mutationFn: async ({
      currentTargetPressId,
      currentSimilarPressId,
      currentSimilarCategoryType,
      currentTargetCategoryType,
    }: {
      currentTargetPressId: number;
      currentSimilarPressId: number;
      currentSimilarCategoryType: "PRESS" | "FILING";
      currentTargetCategoryType: "PRESS" | "FILING";
    }) => {
      const isDifferentCategoryType =
        currentSimilarCategoryType !== currentTargetCategoryType;
      if (isDifferentCategoryType) {
        const filingId =
          currentTargetCategoryType === "FILING"
            ? currentTargetPressId
            : currentSimilarPressId;

        // target 과 similar 데이터가 다른경우
        // filing이 draftArticleId로 설정
        // press가 duplArticleId로 설정
        // 해제 시에는 filingId로 처리

        const response = await unsuitablePressApis.put(String(filingId));
        return response;
      }
      const response = await unsuitablePressApis.put(
        String(currentTargetPressId),
      );
      return response;
    },
    onSuccess: async (response, request) => {
      if (!isPressList) {
        const {
          currentSimilarCategoryType,
          currentTargetCategoryType,
          currentSimilarPressId,
          currentTargetPressId,
        } = request;

        const isDifferentCategoryType =
          currentSimilarCategoryType !== currentTargetCategoryType;
        if (isDifferentCategoryType) {
          const filingId =
            currentTargetCategoryType === "FILING"
              ? currentTargetPressId
              : currentSimilarPressId;

          await queryClient.refetchQueries({
            queryKey: [QUERY_KEYS.articleInfo, Number(filingId)],
          });
        }
        if (!isDifferentCategoryType) {
          const articleId = getPressIdFromURL();
          await queryClient.refetchQueries({
            queryKey: [QUERY_KEYS.articleInfo, Number(articleId)],
          });
        }
      }
      if (response.code !== 200) {
        toastFail(response.message);
        throw new Error(`${response.message}`);
      }
    },
  });

  return mutation;
};
